import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";
import { useSession } from "./session";

type Favorites = { has: (id: string) => boolean; toggle: (id: string) => Promise<void> };
const Ctx = createContext<Favorites>({ has: () => false, toggle: async () => {} });

/** The signed-in user's saved listings, loaded once so every card's heart shows the right state. */
export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const [ids, setIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!me) return setIds(new Set());
    api<{ favorites: { id: string }[] }>("/api/favorites")
      .then((r) => setIds(new Set(r.favorites.map((f) => f.id))))
      .catch(() => {});
  }, [me]);
  const has = useCallback((id: string) => ids.has(id), [ids]);
  const toggle = useCallback(
    async (id: string) => {
      const saved = ids.has(id);
      // Optimistic: the heart changes at once and reverts if the request fails.
      setIds((s) => { const n = new Set(s); if (saved) n.delete(id); else n.add(id); return n; });
      try {
        await api(`/api/favorites/${id}`, { method: saved ? "DELETE" : "PUT" });
      } catch (e) {
        setIds((s) => { const n = new Set(s); if (saved) n.add(id); else n.delete(id); return n; });
        throw e;
      }
    },
    [ids],
  );
  return <Ctx.Provider value={{ has, toggle }}>{children}</Ctx.Provider>;
}

export const useFavorites = () => useContext(Ctx);
